<template>
    <Modal :show="showModal" @close="showModal = false">
        <div class="space-y-8">
            <div class="flex items-center space-x-4">
                <div class="flex h-14 w-14 items-center justify-center rounded-full bg-camaq-700/20 text-camaq-700">
                    <IconMailbox :size="32" />
                </div>

                <p class="text-lg font-medium lg:text-2xl">{{ $t('Subscribe to a job alert') }}</p>
            </div>

            <p class="text-justify text-sm lg:text-base">
                {{
                    $t(
                        "Enter your email address and specify the keywords you're interested in. When an offer matching those keywords is posted, you will receive an email notification. You can unsubscribe at any time with just one click."
                    )
                }}
            </p>

            <form
                @submit.prevent="submit"
                @change="form.errors.clear($event.target.name)"
                @keyup="form.errors.clear($event.target.name)"
                class="space-y-4 lg:space-y-6"
            >
                <div>
                    <label for="email" class="label">
                        {{ $t('Your email address') }}
                        <span class="text-red-600">*</span>
                    </label>
                    <input type="text" name="email" id="email" v-model="form.email" class="input" />
                    <p class="form-error" v-if="form.errors.has('email')" v-text="form.errors.get('email')"></p>
                </div>

                <div class="space-y-2">
                    <label for="keywords" class="label">
                        {{ $t('Keywords') }}
                        <span class="text-red-600">*</span>
                    </label>
                    <p class="text-justify text-sm text-gray-700">
                        {{ $t('Type a keyword (example :') }}
                        <span class="font-bold">Assembleur structure</span>
                        {{ $t('or') }}
                        <span class="font-bold">Mécanicien</span>
                        {{ $t(') and press on ') }}
                        <kbd>{{ $t('Enter') }}</kbd>
                        {{ $t('to add it. You can add multiple keywords. Press the ') }}
                        <kbd>{{ $t('Backspace') }}</kbd>
                        {{ $t(' key to remove a keyword.') }}
                    </p>
                    <Chips
                        v-model="form.keywords"
                        :pt="{
                            root: ({ props }) => ({
                                class: [
                                    'flex',
                                    {
                                        'opacity-60 select-none pointer-events-none cursor-default': props.disabled,
                                    },
                                ],
                            }),
                            container:
                                'm-0 py-1.5 px-3 list-none cursor-text overflow-hidden flex items-center flex-wrap w-full font-sans text-base text-gray-600 bg-white p-3 border border-gray-300 transition-colors duration-200 appearance-none rounded-lg hover:border-gray-700 focus:outline-none focus:outline-offset-0',
                            inputtoken: 'py-1.5 px-0 flex flex-1 inline-flex focus:outline-none outline-none border-0',
                            input: 'font-sans text-base text-gray-700 p-0 m-0 border-0 outline-none bg-transparent shadow-none rounded-none w-full',
                            token: 'py-1 px-2 mr-2 bg-gray-300 text-gray-700 rounded-full cursor-default inline-flex items-center',
                            removeTokenIcon: 'ml-2',
                        }"
                    />
                    <p class="form-error" v-if="form.errors.has('keywords')" v-text="form.errors.get('keywords')"></p>
                </div>

                <p class="text-sm font-medium">{{ $t('Optional : specify a city and a maximum distance') }}</p>

                <div class="grid gap-4 lg:grid-cols-5">
                    <div class="lg:col-span-3">
                        <label for="location" class="label">{{ $t('City') }}</label>
                        <CityAutocomplete v-model="form.location" class="input" @latlng="onLatLng" />
                        <p class="form-error" v-if="form.errors.has('location')" v-text="form.errors.get('location')"></p>
                    </div>

                    <div class="lg:col-span-2">
                        <label for="radius" class="label">{{ $t('Max distance') }}</label>
                        <select name="radius" id="radius" v-model="form.radius" class="input">
                            <option value="10">10 KM</option>
                            <option value="25">25 KM</option>
                            <option value="50">50 KM</option>
                            <option value="100">100 KM</option>
                        </select>
                        <p class="form-error" v-if="form.errors.has('radius')" v-text="form.errors.get('radius')"></p>
                    </div>
                </div>

                <div class="flex justify-center">
                    <Button type="submit" :busy="form.processing">{{ $t('Subscribe to the job alert') }}</Button>
                </div>
            </form>
        </div>
    </Modal>
</template>

<script setup>
    import Chips from 'primevue/chips';
    import Modal from '@/Shared/Modal.vue';
    import Button from '@/Shared/Button.vue';
    import CityAutocomplete from '@/Shared/CityAutocomplete.vue';
    import Form from '@/forms.js';
    import { reactive, ref, watch } from 'vue';
    import Swal from 'sweetalert2';
    import { trans } from 'laravel-vue-i18n';
    import { IconMailbox } from '@tabler/icons-vue';

    const showModal = ref(false);

    const form = reactive(
        new Form({
            email: '',
            keywords: [],
            location: '',
            lat: '',
            lng: '',
            radius: '10',
        })
    );

    watch(
        () => form.keywords,
        () => form.errors.clear('keywords')
    );

    function show() {
        form.keywords = [];
        form.errors.clear('email');
        form.errors.clear('location');
        form.errors.clear('keywords');
        form.errors.clear('radius');

        showModal.value = true;
    }

    function onLatLng(latlng) {
        form.lat = latlng.lat;
        form.lng = latlng.lng;
    }

    function submit() {
        form.post('/alerte-emploi').then(response => {
            showModal.value = false;
            form.keywords = [];
            form.radius = 10;

            Swal.fire({
                title: 'Succès',
                text: trans('Your job alert has been added. Please check your email for a confirmation link.'),
                icon: 'success',
                timer: 3000,
                timerProgressBar: true,
                showConfirmButton: false,
            });
        });
    }

    defineExpose({ show });
</script>
